import React, { useRef, useState } from 'react';
import api from '../services/api';
import CameraCaptureModal from './CameraCaptureModal';
import { generateFoodCardGLB } from '../utils/generateFoodAR';

// Botón para poner la foto de un producto/categoría. Ofrece DOS acciones bien
// diferenciadas en vez de dejar que el navegador decida:
// - "Tomar foto": abre una cámara en vivo (pide permiso, prueba la trasera primero).
// - "Subir archivo": abre el explorador de archivos/galería normal.
//
// Si autoGenerateAR está activo, además de subir la foto genera automáticamente
// un modelo 3D (.glb) a partir de ella y avisa el resultado por onModelGenerated,
// para que la vista AR del cliente funcione sin que el admin tenga que hacer nada más.
const ImageUploadButton = ({ value, onChange, label = 'Imagen', autoGenerateAR = false, onModelGenerated }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [generatingAR, setGeneratingAR] = useState(false);
  const [arReady, setArReady] = useState(false);
  const [error, setError] = useState('');
  const [showCamera, setShowCamera] = useState(false);

  const uploadFile = async (file) => {
    setUploading(true);
    setError('');
    setArReady(false);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await api.post('/uploads/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(res.data.data.url);

      if (autoGenerateAR && onModelGenerated) {
        generateAR(file);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al subir la imagen');
    } finally {
      setUploading(false);
    }
  };

  const generateAR = async (file) => {
    setGeneratingAR(true);
    try {
      const glbBlob = await generateFoodCardGLB(file);
      const formData = new FormData();
      formData.append('model', glbBlob, 'plato.glb');
      const res = await api.post('/uploads/model', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onModelGenerated(res.data.data.url);
      setArReady(true);
    } catch (err) {
      console.error('Error generando el modelo AR:', err);
      // No es bloqueante: el producto se guarda igual, solo sin vista AR
    } finally {
      setGeneratingAR(false);
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) uploadFile(file);
  };

  const handleCameraCapture = (file) => {
    setShowCamera(false);
    uploadFile(file);
  };

  return (
    <div>
      <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>{label}</label>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <div style={{
          width: 64, height: 64, borderRadius: 10, flexShrink: 0,
          background: value ? `url(${value}) center/cover` : '#334155',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
          border: '1px solid #475569',
        }}>
          {!value && '🖼️'}
        </div>
        <div style={{ flex: 1 }}>
          <input
            type="text"
            className="input"
            placeholder="URL de imagen (o toma/sube una foto)"
            value={value || ''}
            onChange={e => onChange(e.target.value)}
            style={{ marginBottom: 6 }}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setShowCamera(true)}
              disabled={uploading}
              className="btn"
              style={{ background: '#334155', color: '#e2e8f0', padding: '6px 12px', fontSize: 12 }}
            >
              📷 Tomar foto
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="btn"
              style={{ background: '#334155', color: '#e2e8f0', padding: '6px 12px', fontSize: 12 }}
            >
              🖼️ Subir archivo
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange('')}
                className="btn"
                style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', padding: '6px 12px', fontSize: 12 }}
              >
                Quitar
              </button>
            )}
          </div>
          {uploading && <p style={{ color: '#94a3b8', fontSize: 11, marginTop: 4 }}>Subiendo...</p>}
          {generatingAR && <p style={{ color: '#a855f7', fontSize: 11, marginTop: 4 }}>🧊 Generando vista AR...</p>}
          {arReady && !generatingAR && <p style={{ color: '#10b981', fontSize: 11, marginTop: 4 }}>✅ Vista AR lista</p>}
          {/* Sin "capture": esto SIEMPRE abre el explorador de archivos/galería, nunca la cámara directamente */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
          {error && <p style={{ color: '#ef4444', fontSize: 11, marginTop: 4 }}>{error}</p>}
        </div>
      </div>

      {showCamera && (
        <CameraCaptureModal
          onCapture={handleCameraCapture}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  );
};

export default ImageUploadButton;